import type { GrammarConfig, Question } from './topics';
import { options, pick, shuffle } from './util';

// ─────────────────────────────────────────────────────────────────────────────
// The verb-tense engine, shared by every language.
//
// A language supplies its people (yo/tú/…, I/you/…) and a list of tenses. A
// tense supplies the verbs that are REGULAR in it and a function giving the
// accepted answer(s) for a verb and a person — first answer is the one shown.
// Restricting each tense to verbs that really are regular in it is what keeps
// rule-built forms correct: an irregular verb is never put through a rule.
// ─────────────────────────────────────────────────────────────────────────────

export type Tense = {
  /** Unique within the language; the topic id is `${lang}_t_${id}`. */
  id: string;
  title: string;
  blurb: string;
  /** verb → English meaning. Only verbs regular in THIS tense. */
  verbs: Record<string, string>;
  /** Accepted answers for verb + person index, or null if it doesn't exist. */
  forms: (verb: string, i: number) => string[] | null;
  /** Which person indices this tense uses (imperatives use a few). */
  people?: number[];
  /** Label for a person in this tense, e.g. "(tú)" for commands, "que yo" for the subjunctive. */
  who?: (i: number, answer: string, defaultWho: string) => string;
  /** Word after the blank, e.g. a time marker in English. */
  after?: string;
};

export type LanguageTenses = {
  lang: string;
  /** Labels per person index; one is picked at random. */
  people: string[][];
  /** A person only included when the teacher ticks it (vosotros, tu). */
  optional?: number;
  tenses: Tense[];
};

export const topicId = (lang: string, tense: string) => `${lang}_t_${tense}` as const;

export function tenseQuestions(L: LanguageTenses, T: Tense, cfg: GrammarConfig, n: number): Question[] {
  const people = (T.people ?? L.people.map((_, i) => i))
    .filter(i => cfg.vosotros || i !== L.optional);
  const verbs = Object.keys(T.verbs);
  const qs: Question[] = [];
  const topic = topicId(L.lang, T.id);
  for (let k = 0, tries = 0; k < n && tries < n * 5; tries++) {
    const verb = pick(verbs);
    const i = pick(people);
    const answers = T.forms(verb, i);
    if (!answers?.length) continue;
    k++;
    const base = pick(L.people[i]);
    const who = T.who ? T.who(i, answers[0], base) : base;
    const typed = Math.random() < 0.5;
    // Wrong options: this verb for the other people, then the same person in
    // the language's other tenses — English's past simple is "worked" for
    // everyone, so other people alone can leave nothing to choose between.
    const otherPeople = people.filter(j => j !== i).map(j => T.forms(verb, j)?.[0] ?? '');
    const otherTenses = L.tenses.filter(o => o !== T && verb in o.verbs).map(o => o.forms(verb, i)?.[0] ?? '');
    const others = [...new Set([...shuffle(otherPeople).slice(0, 2), ...shuffle(otherTenses)])]
      .filter(o => o && !answers.includes(o));
    qs.push({
      key: `${topic}:${verb}:${i}`, topic, kind: typed ? 'type' : 'choice',
      instruction: `${T.title} · ${typed ? 'write' : 'choose'} the verb`,
      prompt: `${who}${who.endsWith("'") ? '' : ' '}___ (${verb})${T.after ? ` ${T.after}` : ''}`,
      hint: T.verbs[verb], answers,
      options: options(answers[0], others.slice(0, 3)),
    });
  }
  return qs;
}

/** Each person's form from a table of six (or five) endings on a stem. */
export const table = (stem: string, endings: string[], i: number) => stem + endings[i];

/** Compound tense: auxiliary form + participle, with agreement variants. */
export function compound(aux: string, participle: string | string[]): string[] {
  const pps = Array.isArray(participle) ? participle : [participle];
  return pps.map(pp => `${aux} ${pp}`);
}
